import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComparisonTable = ({ products, onRemoveProduct, onSaveComparison, onExportPDF }) => {
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    performance: true,
    features: true,
    specifications: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(price);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 4.0) return 'text-blue-600';
    if (rating >= 3.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'bg-green-100 text-green-800';
    if (score >= 80) return 'bg-blue-100 text-blue-800';
    if (score >= 70) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const ComparisonRow = ({ label, icon, children, className = "" }) => (
    <div className={`grid grid-cols-${products?.length + 1} gap-4 py-3 border-b border-border last:border-b-0 ${className}`}>
      <div className="flex items-center space-x-2 font-medium text-text-secondary">
        {icon && <Icon name={icon} size={16} className="text-primary" />}
        <span className="text-sm">{label}</span>
      </div>
      {children}
    </div>
  );

  const SectionHeader = ({ title, section, icon }) => (
    <div 
      className="flex items-center justify-between p-4 bg-muted cursor-pointer hover:bg-muted/80 transition-colors"
      onClick={() => toggleSection(section)}
    >
      <div className="flex items-center space-x-2">
        <Icon name={icon} size={18} className="text-primary" />
        <h3 className="font-semibold text-text-primary">{title}</h3>
      </div>
      <Icon 
        name={expandedSections?.[section] ? "ChevronUp" : "ChevronDown"} 
        size={16} 
        className="text-muted-foreground" 
      />
    </div>
  );

  if (products?.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-border p-8 text-center">
        <Icon name="GitCompare" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          No Products Selected
        </h3>
        <p className="text-muted-foreground">
          Add products from the list to start comparing their features and specifications.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-border overflow-hidden">
      {/* Header Actions */}
      <div className="p-4 border-b border-border bg-surface">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">
            Comparing {products?.length} Products
          </h2>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Save"
              iconPosition="left"
              onClick={onSaveComparison}
            >
              Save Comparison
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={onExportPDF}
            >
              Export PDF
            </Button>
          </div>
        </div>
      </div>
      {/* Product Headers */}
      <div className={`grid grid-cols-${products?.length + 1} gap-4 p-4 bg-surface border-b border-border`}>
        <div></div>
        {products?.map((product) => (
          <div key={product?.id} className="text-center">
            <div className="relative mb-3">
              <Button
                variant="ghost"
                size="xs"
                iconName="X"
                onClick={() => onRemoveProduct(product?.id)}
                className="absolute -top-2 -right-2 text-muted-foreground hover:text-destructive z-10"
              />
              <div className="w-20 h-20 bg-white rounded-lg border border-border overflow-hidden mx-auto">
                <Image
                  src={product?.image}
                  alt={product?.imageAlt}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <h4 className="font-semibold text-sm text-text-primary mb-1">
              {product?.name}
            </h4>
            <p className="text-xs text-muted-foreground mb-2">
              {product?.brand}
            </p>
            <div className="text-lg font-bold text-primary">
              {formatPrice(product?.currentPrice)}
            </div>
            {product?.originalPrice > product?.currentPrice && (
              <div className="text-xs text-muted-foreground line-through">
                {formatPrice(product?.originalPrice)}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Basic Information */}
      <div>
        <SectionHeader title="Basic Information" section="basic" icon="Info" />
        {expandedSections?.basic && (
          <div className="p-4 space-y-0">
            <ComparisonRow label="Overall Rating" icon="Star">
              {products?.map((product) => (
                <div key={product?.id} className="text-center">
                  <div className={`text-lg font-bold ${getRatingColor(product?.rating)}`}>
                    {product?.rating}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    ({product?.reviewCount} reviews)
                  </div>
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Performance Score" icon="Zap">
              {products?.map((product) => (
                <div key={product?.id} className="text-center">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(product?.performanceScore)}`}>
                    {product?.performanceScore}/100
                  </span>
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Type" icon="Package">
              {products?.map((product) => (
                <div key={product?.id} className="text-center text-sm text-text-primary">
                  {product?.type}
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Best For" icon="Target">
              {products?.map((product) => (
                <div key={product?.id} className="text-center text-sm text-muted-foreground">
                  {product?.bestFor}
                </div>
              ))}
            </ComparisonRow>
          </div>
        )}
      </div>
      {/* Performance Metrics */}
      <div>
        <SectionHeader title="Performance Metrics" section="performance" icon="BarChart3" />
        {expandedSections?.performance && (
          <div className="p-4 space-y-0">
            <ComparisonRow label="Suction Power" icon="Wind">
              {products?.map((product) => (
                <div key={product?.id} className="text-center">
                  <div className="text-sm font-medium text-text-primary">
                    {product?.suctionPower} AW
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Air Watts
                  </div>
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Carpet Cleaning" icon="Home">
              {products?.map((product) => (
                <div key={product?.id} className="text-center">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getScoreColor(product?.carpetScore)}`}>
                    {product?.carpetScore}%
                  </span>
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Hard Floor Cleaning" icon="Square">
              {products?.map((product) => (
                <div key={product?.id} className="text-center">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getScoreColor(product?.hardFloorScore)}`}>
                    {product?.hardFloorScore}%
                  </span>
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Pet Hair Removal" icon="Heart">
              {products?.map((product) => (
                <div key={product?.id} className="text-center">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getScoreColor(product?.petHairScore)}`}>
                    {product?.petHairScore}%
                  </span>
                </div>
              ))}
            </ComparisonRow>
          </div>
        )}
      </div>
      {/* Features */}
      <div>
        <SectionHeader title="Features & Usability" section="features" icon="Settings" />
        {expandedSections?.features && (
          <div className="p-4 space-y-0">
            <ComparisonRow label="Bagless Design" icon="Package">
              {products?.map((product) => (
                <div key={product?.id} className="text-center">
                  <Icon 
                    name={product?.bagless ? "Check" : "X"} 
                    size={16} 
                    className={product?.bagless ? "text-green-600" : "text-red-600"} 
                  />
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="HEPA Filter" icon="Shield">
              {products?.map((product) => (
                <div key={product?.id} className="text-center">
                  <Icon 
                    name={product?.hepaFilter ? "Check" : "X"} 
                    size={16} 
                    className={product?.hepaFilter ? "text-green-600" : "text-red-600"} 
                  />
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Cord Length" icon="Cable">
              {products?.map((product) => (
                <div key={product?.id} className="text-center text-sm text-text-primary">
                  {product?.cordLength}ft
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Warranty" icon="Shield">
              {products?.map((product) => (
                <div key={product?.id} className="text-center text-sm text-text-primary">
                  {product?.warranty}
                </div>
              ))}
            </ComparisonRow>
          </div>
        )}
      </div>
      {/* Detailed Specifications */}
      <div>
        <SectionHeader title="Detailed Specifications" section="specifications" icon="FileText" />
        {expandedSections?.specifications && (
          <div className="p-4 space-y-0">
            <ComparisonRow label="Power" icon="Zap">
              {products?.map((product) => (
                <div key={product?.id} className="text-center text-sm text-text-primary">
                  {product?.power}W
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Weight" icon="Weight">
              {products?.map((product) => (
                <div key={product?.id} className="text-center text-sm text-text-primary">
                  {product?.weight} lbs
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Noise Level" icon="Volume2">
              {products?.map((product) => (
                <div key={product?.id} className="text-center text-sm text-text-primary">
                  {product?.noiseLevel} dB
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Dust Capacity" icon="Container">
              {products?.map((product) => (
                <div key={product?.id} className="text-center text-sm text-text-primary">
                  {product?.capacity}L
                </div>
              ))}
            </ComparisonRow>

            <ComparisonRow label="Dimensions" icon="Maximize">
              {products?.map((product) => (
                <div key={product?.id} className="text-center text-sm text-text-primary">
                  {product?.dimensions}
                </div>
              ))}
            </ComparisonRow>
          </div>
        )}
      </div>
      {/* Action Buttons */}
      <div className="p-4 border-t border-border bg-surface">
        <div className={`grid grid-cols-${products?.length + 1} gap-4`}>
          <div></div>
          {products?.map((product) => (
            <div key={product?.id} className="space-y-2">
              <Button
                variant="default"
                size="sm"
                iconName="ExternalLink"
                iconPosition="right"
                fullWidth
              >
                View Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Bell"
                iconPosition="left"
                fullWidth
              >
                Price Alert
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComparisonTable;